import { languages } from "locales/languages"
import { Input } from "components/Input"
import { useEffect, useState } from "react"
import Button from "components/Button/Button/Index"
import { Buttons } from "components/Card/Card"
import { useDispatch, useSelector } from "react-redux"
import { Keyboard } from "hocs/withKeyboard"
import { Customer, FamilyDoctor } from "types/entity"
import { useNavigate } from "react-router"
import { SURVEY_PAGE } from "core/constants"
import PreviousButton from "components/Previous/PreviousButton"
import { Title } from "components/Title/Title"
import { Card } from "components/Card/Card"
import { TreatingDoctorIncitationModal } from "components/Modal/Modal"
import { SEARCH_DOCTORS, RESET_DOCTOR_LIST, DOCTOR_PROFESSION } from "./services/constants"

import styles from "./DoctorSearch.module.scss"
import { Carousel } from "components/Carousel/Index"
import { DoctorSearchStore } from "types/store"
import { getCurrentPatient } from "containers/Survey/services/selector"
import { CHANGE_RELATIVE_REQUEST, CHANGE_PATIENT_REQUEST } from "containers/Profile/services/constants"
import { PREVIOUS_QUESTION } from "containers/Survey/services/constants"

export const MIN_SEARCH_LENGTH = 2

export const DoctorCard = ({ doctor, onClick }) => {
    const fullName = `${doctor.firstname} ${doctor.lastname}`;
    const isAddress = !!doctor.libelle_commune_coord_structure;
    const address = isAddress
        ? doctor.libelle_commune_coord_structure.replace(/ Arrondissement$/i, '')
        : (doctor.libelle_savoir_faire || languages.addressNotProvided);
    const isItalic = isAddress || !doctor.libelle_savoir_faire;

    return <div className={styles.DoctorCard} onClick={onClick}>
        <span className={styles.doctorName}>{fullName}</span>
        <span className={`${styles.doctorAddress} ${isItalic ? styles.doctorAddressItalic : ""}`}>
          {address}
        </span>
    </div>
}

type DoctorsResultsProps =  {
  doctors: FamilyDoctor[]
  selectDoctor: (doctor: FamilyDoctor) => void
  onNearEnd?: () => void
  query?: string
  loading?: boolean
}

export const DoctorsResults = (props: DoctorsResultsProps) => {
    const queryLength = (props.query ?? "").trim().length
    const hasNoDoctors = !props.doctors || !props.doctors.length
    return <div >
        {props.loading && hasNoDoctors
          ? null
          : hasNoDoctors
          ? <div className={styles.NoResult}>
              {queryLength === 0
                ? languages.searchDoctorInstruction
                : queryLength < MIN_SEARCH_LENGTH
                ? languages.searchMinLengthInstruction
                : queryLength === MIN_SEARCH_LENGTH
                ? languages.searchDoctorInstruction
                : languages.noDoctorsFound}
            </div>
          : <Carousel
              itemsPerPage={3}
              items={props.doctors.map((doctor, key) =>
                  <DoctorCard key={key} doctor={doctor}
                      onClick={() => { props.selectDoctor(doctor) }}
                  />
              )}
              infinite={false}
              onNearEnd={props.onNearEnd}
          />}
      </div>
}

export const DoctorsSearch = (props: any) => {
    const [request, setRequest] = useState("")
    const [showIncitationModal, setShowIncitationModal] = useState(false)
    const [isPendingSearch, setIsPendingSearch] = useState(false)
    const {doctors = [], hasMore = false, currentPage = 1, loading = false} = useSelector(({ searchedDoctors }: { searchedDoctors: DoctorSearchStore }) => searchedDoctors);
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const patient = useSelector(getCurrentPatient)

    useEffect(() => {
      if (request.length >= MIN_SEARCH_LENGTH) {
          setIsPendingSearch(true)
          const delayKeyStrokeFn = setTimeout(() => {
            setIsPendingSearch(false)
            dispatch({ type: SEARCH_DOCTORS, payload: {
              fullname: request.trim(),
              profession: DOCTOR_PROFESSION,
            } });
        }, 200);
        return () => clearTimeout(delayKeyStrokeFn);
      } else {
          setIsPendingSearch(false)
          dispatch({ type: RESET_DOCTOR_LIST });
      }}, [request]
    );

    const onNearEnd = hasMore && !loading ? () => {
      dispatch({ type: SEARCH_DOCTORS, payload: {
        fullname: request.trim(),
        profession: DOCTOR_PROFESSION,
        page: currentPage + 1,
      }})
    } : undefined

    const results: JSX.Element = DoctorsResults({ doctors: doctors, onNearEnd, query: request, loading: loading || isPendingSearch, selectDoctor: (doctor) => {
      if (doctor?.rpps) {
        (patient as Customer).email ?
          dispatch({ type: CHANGE_PATIENT_REQUEST, payload: { family_doctor_rpps: doctor.rpps }})
          : dispatch({ type: CHANGE_RELATIVE_REQUEST, payload: { id: patient.id, family_doctor_rpps: doctor.rpps }})
      }
      navigate(SURVEY_PAGE)
    }});
    
    return <Card>
        <PreviousButton onClick={() =>  {
            dispatch({type: RESET_DOCTOR_LIST});
            dispatch({type: PREVIOUS_QUESTION});
            navigate(SURVEY_PAGE)
          }}
        />
        <Title size="small" text={languages.treatingDoctor} />
        <div >
            <Keyboard
                options={{
                    type: "text",
                    inputName: "request",
                    resultsDisplay: results,
                }}
                value={request}
                onChange={(value) => { setRequest(value) }}>
              <Input
                  placeholder={languages.requestDoctorPlaceholder}
                  style={{ marginBottom: "50px" }}
                  value={request}
                  name="request"
              />
            </Keyboard>
            <Buttons>
              <Button wide="default" htmlType="button" onClick={() => {
                  const hasDoctor = Object.keys((patient as Customer)?.family_doctor ?? {}).length > 0
                  if (!hasDoctor) {
                    setShowIncitationModal(true)
                  } else {
                    dispatch({type: RESET_DOCTOR_LIST})
                    navigate(SURVEY_PAGE)
                  }
                }}>
                {languages.skipStep}
              </Button>
            </Buttons>
        </div>
        <TreatingDoctorIncitationModal
          visible={showIncitationModal}
          onEnterDoctor={() => setShowIncitationModal(false)}
          onSkip={() => {
            setShowIncitationModal(false)
            dispatch({type: RESET_DOCTOR_LIST})
            navigate(SURVEY_PAGE)
          }}
        />
    </Card>
}
